import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Spin } from 'antd';
import Form from '../../form/Form';
import { FormInstance } from 'antd/lib/form';
import { useEvents } from '../../connection/Application';

export type FormModalError = { name: string; errors: string[] };
export type FormModalErrors = FormModalError[];

export class FormModelInstance<T> {
  _form: FormInstance<T> | undefined = undefined;

  get form(): FormInstance<T> {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this._form!;
  }

  open = (value?: Partial<T>) => {
    console.error('No form modal reference set', value);
  };

  close = () => {
    console.error('No form modal reference set');
  };

  setErrors = (errors: FormModalErrors) => {
    console.error('No form modal reference set', errors);
  };

  reset = (value?: T) => {
    console.error('No form modal reference set', value);
  };

  submit = () => {
    this.form.submit();
  };
}

type Props<T = any> = {
  title: string;
  // visible: boolean;
  onSubmit: (value: T) => void;
  form: FormModelInstance<T>;
  onClose?: () => void;
  // initialValues?: any;
  // errors?: { name: string; errors: string[] }[];
  okText?: string;
  initialValues?: any;
  width?: string | number;
};

export function useFormModal<T = any>() {
  return useRef(new FormModelInstance<T>()).current;
}

export function FormModal<T = any>(props: PropsWithChildren<Props<T>>) {
  const [formInstance] = Form.useForm<T>();
  const { form, title, onSubmit } = props;
  const [visible, setVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>();
  const [selectValuesLoadingCount, setSelectValuesLoadingCount] = useState<number>(0);
  const events = useEvents();
  const selectValuesLoading = visible && selectValuesLoadingCount > 0;
  // useEffect(() => {
  //   formInstance.setFieldsValue(props.initialValues);
  // }, [formInstance, props.initialValues]);
  //
  // useEffect(() => {
  //   if (props.errors) {
  //     formInstance.setFields((props.errors as any) ?? []);
  //   }
  // }, [formInstance, props.errors]);
  const handleSubmit = useCallback(
    (values: T) => {
      setLoading(true);
      onSubmit(values);
    },
    [onSubmit],
  );

  useEffect(() => {
    form._form = formInstance;
    form.open = (value) => {
      formInstance.resetFields();
      if (value) {
        formInstance.setFieldsValue(value as any);
      }
      setVisible(true);
      setLoading(false);
    };
    form.close = () => {
      setLoading(false);
      setVisible(false);
    };
    form.setErrors = (errors) => {
      formInstance.setFields(errors);
      setLoading(false);
    };
    form.reset = (value) => {
      formInstance.resetFields();
      if (value) {
        formInstance.setFieldsValue(value as any);
      }
    };
    return () => {
      form.open = () => {
        console.log('Not implemented');
      };
      form.close = () => {
        console.log('Not implemented');
      };
      form.setErrors = () => {
        console.log('Not implemented');
      };
      form.reset = () => {
        console.log('Not implemented');
      };
    };
  }, [form, formInstance]);

  useEffect(() => {
    const handler = (count: number) => {
      setSelectValuesLoadingCount(Math.max(0, count ?? 0));
    };
    events.on('SELECT_VALUES_LOADING', handler);
    return () => {
      events.off('SELECT_VALUES_LOADING', handler);
    };
  }, [events]);

  return (
    <Modal
      width={props.width}
      title={title}
      open={visible}
      closable={true}
      destroyOnClose={true}
      onCancel={() => {
        setLoading(!loading);
        setVisible(false);
        props?.onClose?.();
      }}
      onOk={formInstance.submit}
      okText={props.okText ?? 'Save'}
      confirmLoading={loading || selectValuesLoading}>
      <Spin spinning={selectValuesLoading}>
        <Form
          form={formInstance}
          onFinish={handleSubmit}
          layout={'vertical'}
          initialValues={props?.initialValues}>
          {props.children}
        </Form>
      </Spin>
    </Modal>
  );
}
